import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import { Loader2, Check, GraduationCap, Search, X, Pencil } from 'lucide-react';
import { promoService } from '../../services/api';
import UserAvatar from '../../components/common/UserAvatar';
import AvatarPickerModal from '../../components/common/AvatarPickerModal';
import { CANADIAN_PROVINCES } from '../../data/canadianProvinces';

/** Resolve a province value (code OR label in any language) to its 2-letter code. */
const resolveProvinceCode = (val) => {
    if (!val) return '';
    const upper = val.trim().toUpperCase();
    // Already a valid code?
    const byCode = CANADIAN_PROVINCES.find(p => p.code === upper);
    if (byCode) return byCode.code;
    // Match by French or English label (case-insensitive)
    const lower = val.trim().toLowerCase();
    const byLabel = CANADIAN_PROVINCES.find(
        p => p.fr.toLowerCase() === lower || p.en.toLowerCase() === lower
    );
    return byLabel ? byLabel.code : val;
};

const DocumentIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const LockIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

const EyeIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const fieldClass = "bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-white/5 rounded-2xl px-5 py-4 text-gray-900 dark:text-white font-medium focus-within:border-purple-500/50 transition-colors w-full";
const inputClass = "bg-transparent w-full outline-none placeholder-gray-400";
const labelClass = "text-[11px] font-bold text-gray-500 dark:text-gray-500 uppercase tracking-widest ml-1";

const PasswordField = ({ label, placeholder, value, onChange, autoComplete }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-2">
            <label className={labelClass}>{label}</label>
            <div className={`${fieldClass} flex items-center gap-3`}>
                <input
                    type={show ? "text" : "password"}
                    placeholder={placeholder}
                    value={value ?? ''}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    className={inputClass}
                />
                <button type="button" onClick={() => setShow(!show)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                    <EyeIcon className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};

const UserInfo = () => {
    const { user, setUser } = useAuth();
    const { language } = useLanguage();

    // Profile form state
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', phone: '',
        address: '', address2: '', postalCode: '',
        city: '', province: '', country: 'Canada', flightSchool: ''
    });
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [showAvatarPicker, setShowAvatarPicker] = useState(false);

    // School code state
    const [schoolCodeInput, setSchoolCodeInput] = useState('');
    const [schoolVerified, setSchoolVerified] = useState(null); // { code, name, percentage } or null
    const [schoolChecking, setSchoolChecking] = useState(false);
    const [schoolError, setSchoolError] = useState('');

    // Password change state
    const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
    const [pwSaving, setPwSaving] = useState(false);
    const [pwSuccess, setPwSuccess] = useState(false);
    const [pwError, setPwError] = useState('');

    // Init form from user context — re-run when full profile data arrives
    // (login response is partial; getProfile() enriches user with address, phone, etc.)
    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                phone: user.phone || '',
                address: user.address || '',
                address2: user.address2 || '',
                postalCode: user.postalCode || '',
                city: user.city || '',
                province: resolveProvinceCode(user.province),
                country: user.country || 'Canada',
                flightSchool: user.flightSchool || ''
            });
            if (user.schoolCode) {
                setSchoolCodeInput(user.schoolCode);
                setSchoolVerified({ code: user.schoolCode, name: user.schoolName });
            }
        }
    }, [user?.id, user?.address, user?.phone, user?.city, user?.province, user?.postalCode, user?.country]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updated = { ...formData, [name]: value };
        if (name === 'province') {
            updated.country = value === 'XX' ? '' : 'Canada';
        }
        setFormData(updated);
        setSaveSuccess(false);
    };

    const handleVerifySchoolCode = async () => {
        const code = schoolCodeInput.trim();
        if (!code) return;
        setSchoolChecking(true);
        setSchoolError('');
        try {
            const result = await promoService.validate(code);
            if (result.valid && result.type === 'school') {
                setSchoolVerified({ code: result.code, name: result.nomEcole, percentage: result.percentage });
                setFormData(prev => ({ ...prev, flightSchool: result.nomEcole }));
            } else {
                setSchoolError(language === 'fr' ? 'Code école non reconnu' : 'School code not recognized');
                setSchoolVerified(null);
            }
        } catch {
            setSchoolError(language === 'fr' ? 'Code école non reconnu' : 'School code not recognized');
            setSchoolVerified(null);
        } finally {
            setSchoolChecking(false);
        }
    };

    const handleClearSchoolCode = () => {
        setSchoolCodeInput('');
        setSchoolVerified(null);
        setSchoolError('');
        setFormData(prev => ({ ...prev, flightSchool: '' }));
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        setSaveSuccess(false);
        try {
            const payload = { ...formData, schoolCode: schoolVerified?.code || '' };
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/user/complete-profile.php`,
                payload,
                { withCredentials: true }
            );
            if (response.data.success) {
                setUser({ ...user, ...response.data.user, profileCompleted: true, name: response.data.user.firstName + ' ' + response.data.user.lastName });
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setSaveError(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving profile');
            }
        } catch (err) {
            setSaveError(err.response?.data?.error || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
        } finally {
            setSaving(false);
        }
    };

    const handlePwChange = (field) => (e) => {
        const value = e.target.value;
        setPwData(prev => ({ ...prev, [field]: value }));
        setPwError('');
        setPwSuccess(false);
    };

    const handleSavePassword = async (e) => {
        e.preventDefault();
        setPwError('');
        setPwSuccess(false);

        // Client-side validation (backend re-validates everything)
        if (!pwData.current || !pwData.newPw || !pwData.confirm) {
            setPwError(language === 'fr' ? 'Veuillez remplir tous les champs.' : 'Please fill in all fields.');
            return;
        }
        if (pwData.newPw.length < 6) {
            setPwError(language === 'fr'
                ? 'Le nouveau mot de passe doit contenir au moins 6 caractères.'
                : 'New password must be at least 6 characters.');
            return;
        }
        if (pwData.newPw !== pwData.confirm) {
            setPwError(language === 'fr'
                ? 'Les deux mots de passe ne correspondent pas.'
                : 'The two passwords do not match.');
            return;
        }

        setPwSaving(true);
        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/user/change-password.php`,
                { currentPassword: pwData.current, newPassword: pwData.newPw, language },
                { withCredentials: true }
            );
            if (response.data.success) {
                setPwSuccess(true);
                setPwData({ current: '', newPw: '', confirm: '' });
                setTimeout(() => setPwSuccess(false), 4000);
            } else {
                setPwError(response.data.error || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
            }
        } catch (err) {
            // Backend returns a localized message (e.g. wrong current password) in err.response.data.error
            setPwError(err.response?.data?.error || (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'));
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <div className="pb-12">
            {/* Header Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#2e1065] via-[#1e1b4b] to-[#0f172a] border border-white/5 !mb-12 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />

                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-600 rounded-2xl shadow-lg shadow-purple-500/20">
                            <DocumentIcon className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">
                            {language === 'fr' ? 'Mes infos' : 'My info'}
                        </h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-sm leading-snug">
                        {language === 'fr'
                            ? 'Gérez vos informations personnelles et sécurisez votre compte'
                            : 'Manage your personal information and secure your account'}
                    </p>
                </div>

                <div className="absolute right-[-40px] bottom-[-40px] opacity-20">
                    <DocumentIcon className="w-80 h-80 text-purple-500 stroke-[0.5]" />
                </div>
            </div>

            {/* Profile Bar */}
            <div className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[1.5rem] p-6 flex items-center justify-between !mb-12 shadow-sm dark:shadow-none">
                <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                    <button
                        onClick={() => setShowAvatarPicker(true)}
                        className="relative flex-shrink-0 group"
                        title={language === 'fr' ? 'Changer d\'avatar' : 'Change avatar'}
                    >
                        <UserAvatar size="xl" />
                        <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <Pencil className="w-5 h-5 text-white" />
                        </div>
                    </button>
                    <div className="min-w-0">
                        <h2 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white tracking-tight truncate">{user?.name}</h2>
                        <p className="text-xs sm:text-sm text-gray-500 font-medium uppercase tracking-wider sm:tracking-widest truncate">{user?.email}</p>
                    </div>
                </div>
            </div>

            {/* Avatar Picker Modal */}
            {showAvatarPicker && (
                <AvatarPickerModal onClose={() => setShowAvatarPicker(false)} />
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 !gap-12">
                {/* Personal Info */}
                <form onSubmit={handleSaveProfile} className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] p-8 space-y-8 shadow-sm dark:shadow-none">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-6 bg-purple-500 rounded-full" />
                        <h3 className="text-lg font-black text-gray-900 dark:text-white">{language === 'fr' ? 'Mes infos' : 'My info'}</h3>
                    </div>

                    {saveError && (
                        <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                            <p className="text-xs text-red-600 dark:text-red-400">{saveError}</p>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Prénom & Nom */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className={labelClass}>{language === 'fr' ? 'Prénom' : 'First Name'} <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>{language === 'fr' ? 'Nom' : 'Last Name'} <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} />
                                </div>
                            </div>
                        </div>

                        {/* Téléphone */}
                        <div className="space-y-2">
                            <label className={labelClass}>{language === 'fr' ? 'Téléphone' : 'Phone'} <span className="text-red-400">*</span></label>
                            <div className={fieldClass}>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass}
                                    placeholder="(XXX) XXX-XXXX" />
                            </div>
                        </div>

                        {/* Adresse */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2 space-y-2">
                                <label className={labelClass}>{language === 'fr' ? 'Adresse' : 'Address'} <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <input type="text" name="address" value={formData.address} onChange={handleChange} required className={inputClass} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>{language === 'fr' ? 'Code Postal' : 'Postal Code'} <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required className={inputClass}
                                        placeholder="A1B 2C3" />
                                </div>
                            </div>
                        </div>

                        {/* Adresse 2 */}
                        <div className="space-y-2">
                            <label className={labelClass}>{language === 'fr' ? 'Adresse (suite)' : 'Address line 2'}</label>
                            <div className={fieldClass}>
                                <input type="text" name="address2" value={formData.address2} onChange={handleChange} className={inputClass}
                                    placeholder={language === 'fr' ? 'Appartement, bureau, etc.' : 'Apartment, office, etc.'} />
                            </div>
                        </div>

                        {/* Ville & Province */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className={labelClass}>{language === 'fr' ? 'Ville' : 'City'} <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <input type="text" name="city" value={formData.city} onChange={handleChange} required className={inputClass} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Province <span className="text-red-400">*</span></label>
                                <div className={fieldClass}>
                                    <select name="province" value={formData.province} onChange={handleChange} required
                                        className={`${inputClass} cursor-pointer`}>
                                        <option value="" className="text-gray-900 bg-white dark:text-gray-900 dark:bg-white">{language === 'fr' ? 'Sélectionnez...' : 'Select...'}</option>
                                        {CANADIAN_PROVINCES.map(p => (
                                            <option key={p.code} value={p.code} className="text-gray-900 bg-white dark:text-gray-900 dark:bg-white">
                                                {language === 'fr' ? p.fr : p.en}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Pays */}
                        <div className="space-y-2">
                            <label className={labelClass}>{language === 'fr' ? 'Pays' : 'Country'} <span className="text-red-400">*</span></label>
                            <div className={`${fieldClass} ${formData.province !== 'XX' ? 'opacity-60' : ''}`}>
                                <input type="text" name="country" value={formData.country} onChange={handleChange} required
                                    readOnly={formData.province !== 'XX'}
                                    className={inputClass} />
                            </div>
                        </div>

                        {/* Code École */}
                        <div className="space-y-2">
                            <label className={labelClass}>{language === 'fr' ? 'Code école' : 'School code'}</label>
                            <div className={`${fieldClass} flex items-center gap-2`}>
                                <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
                                <input
                                    type="text"
                                    value={schoolCodeInput}
                                    onChange={(e) => { setSchoolCodeInput(e.target.value); setSchoolError(''); }}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleVerifySchoolCode())}
                                    className={inputClass}
                                    placeholder={language === 'fr' ? 'Entrez votre code école' : 'Enter your school code'}
                                    disabled={!!schoolVerified}
                                />
                                {schoolVerified ? (
                                    <button type="button" onClick={handleClearSchoolCode}
                                        className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
                                        <X className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button type="button" onClick={handleVerifySchoolCode} disabled={schoolChecking || !schoolCodeInput.trim()}
                                        className="text-gray-400 hover:text-purple-500 transition-colors disabled:opacity-30 shrink-0">
                                        {schoolChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                    </button>
                                )}
                            </div>
                            {schoolVerified && (
                                <div className="flex items-center gap-2 px-1">
                                    <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-2 py-0.5 rounded-full">
                                        {schoolVerified.name}
                                    </span>
                                    {schoolVerified.percentage && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            -{schoolVerified.percentage}% {language === 'fr' ? 'sur vos achats' : 'on your purchases'}
                                        </span>
                                    )}
                                </div>
                            )}
                            {schoolError && (
                                <p className="text-xs text-red-500 px-1">{schoolError}</p>
                            )}
                        </div>

                        {/* École de pilotage */}
                        <div className="space-y-2">
                            <label className={labelClass}>{language === 'fr' ? 'École de pilotage' : 'Flight school'}</label>
                            <div className={`${fieldClass} ${schoolVerified ? 'opacity-60' : ''}`}>
                                <input type="text" name="flightSchool" value={formData.flightSchool} onChange={handleChange}
                                    className={inputClass} readOnly={!!schoolVerified}
                                    placeholder={language === 'fr' ? 'Nom de votre école (optionnel)' : 'School name (optional)'} />
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 text-center">
                        <button
                            type="submit"
                            disabled={saving}
                            className={`w-full py-4 rounded-full font-bold text-sm border transition-all flex items-center justify-center gap-2 ${
                                saveSuccess
                                    ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-400'
                                    : 'bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white border-gray-200 dark:border-white/10 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:border-purple-200 dark:hover:border-purple-500/30 hover:text-purple-700 dark:hover:text-purple-400'
                            } disabled:opacity-50`}
                        >
                            {saving ? (
                                <><Loader2 size={16} className="animate-spin" /> {language === 'fr' ? 'Enregistrement...' : 'Saving...'}</>
                            ) : saveSuccess ? (
                                <><Check size={16} /> {language === 'fr' ? 'Informations mises à jour' : 'Information updated'}</>
                            ) : (
                                language === 'fr' ? 'Mettre à jour mes informations' : 'Update my information'
                            )}
                        </button>
                    </div>
                </form>

                {/* Password Change */}
                <form onSubmit={handleSavePassword} className="bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/5 rounded-[2rem] p-8 space-y-8 shadow-sm dark:shadow-none">
                    <div className="flex items-center gap-3">
                        <LockIcon className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-black text-gray-900 dark:text-white">{language === 'fr' ? 'Mot de passe' : 'Password'}</h3>
                    </div>

                    {pwError && (
                        <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                            <p className="text-xs text-red-600 dark:text-red-400">{pwError}</p>
                        </div>
                    )}
                    {pwSuccess && (
                        <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-xl">
                            <p className="text-xs text-green-700 dark:text-green-400 flex items-center gap-1.5">
                                <Check size={14} />
                                {language === 'fr' ? 'Mot de passe mis à jour avec succès.' : 'Password updated successfully.'}
                            </p>
                        </div>
                    )}

                    <div className="space-y-6">
                        <PasswordField
                            label={language === 'fr' ? 'Mot de passe actuel' : 'Current password'}
                            placeholder="••••••••••••"
                            value={pwData.current}
                            onChange={handlePwChange('current')}
                            autoComplete="current-password"
                        />
                        <div className="h-px bg-gray-100 dark:bg-white/5 w-full !my-8" />
                        <PasswordField
                            label={language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
                            placeholder="••••••••••••"
                            value={pwData.newPw}
                            onChange={handlePwChange('newPw')}
                            autoComplete="new-password"
                        />
                        <PasswordField
                            label={language === 'fr' ? 'Confirmer le nouveau mot de passe' : 'Confirm new password'}
                            placeholder="••••••••••••"
                            value={pwData.confirm}
                            onChange={handlePwChange('confirm')}
                            autoComplete="new-password"
                        />
                    </div>

                    <div className="pt-4 text-center">
                        <button
                            type="submit"
                            disabled={pwSaving}
                            className="w-full py-4 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-black text-sm shadow-xl shadow-purple-500/10 hover:scale-[1.02] transition-transform active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                        >
                            {pwSaving ? (
                                <><Loader2 size={16} className="animate-spin" /> {language === 'fr' ? 'Enregistrement...' : 'Saving...'}</>
                            ) : (
                                language === 'fr' ? 'Enregistrer le nouveau mot de passe' : 'Save new password'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserInfo;
